<script setup lang="ts">
import UiChip from '@boats/ui/components/UiChip.vue'
import { formatCurrency } from '~/utils'

/**
 * The shared "SAVE {amount}" badge for the discounted-products feature
 * (BOAT-1738), reused on product pages, the cart and checkout. Built on UiChip so
 * it inherits the pill shape/typography, recoloured to the money-saved green.
 *
 * `saved` is the money off at the relevant granularity — per ticket on the
 * product page, per line (× quantity) in the cart/checkout. Only render it when
 * the API has flagged a genuine, Omnibus-compliant drop; never derive the drop
 * client-side beyond this simple was − now subtraction of display prices.
 */
type Props = {
  saved: number
  class?: string
}

const props = defineProps<Props>()
</script>

<template>
  <UiChip
    :class="['price-drop-badge !text-white', props.class]"
  >
    {{ $t('string.Save-amount') }} {{ formatCurrency(saved) }}
  </UiChip>
</template>

<style scoped lang="scss">
@import '@boats/ui/styles/config';

.price-drop-badge {
  align-items: center;
  white-space: nowrap;
  font-weight: 600;
  // Darker forest green from the design mockup (the brand
  // signal-positive-green #54d008 lime reads too bright here).
  background-color: #3f8517 !important;
  // 25% smaller than the stock UiChip pill (font-size + padding scaled down
  // together so the whole badge shrinks proportionally) — BOAT-1738 design tweak.
  padding: 0.28rem 0.375rem !important;
  font-size: 0.47rem !important;

  @include media-breakpoint-up(md) {
    padding: 0.28rem 0.47rem !important;
    font-size: 0.66rem !important;
  }
}
</style>
